import AddIcon from '@mui/icons-material/Add';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ComplementoCarrinho,
  ComposicaoItem,
  GrupoPublico,
  ItemCarrinho,
  ProdutoCardapio,
  SelecaoGrupo,
} from '../delivery.types';
import { useDeliveryStore } from '../store/deliveryStore';
import { fmtMoeda } from '../utils';

interface Props {
  produto: ProdutoCardapio;
  onClose: () => void;
  onVerCarrinho?: () => void;
  itemEditando?: ItemCarrinho;
}

function complementosIniciais(produto: ProdutoCardapio): ComposicaoItem[] {
  return produto.composicao.map((c) => ({ ...c, selecionado: c.tipo === 'INGREDIENTE' }));
}

// Retorna os codigos selecionados num grupo
function selecoesDe(selecoes: Map<number, Set<number>>, codigoGrupo: number): Set<number> {
  return selecoes.get(codigoGrupo) ?? new Set();
}

export default function DialogProduto({ produto, onClose, itemEditando }: Props) {
  const { adicionarItem, atualizarItem, setStep } = useDeliveryStore();

  // pré-popula complementos a partir do item existente (edição)
  function complementosDoItem(): ComposicaoItem[] {
    if (!itemEditando) return complementosIniciais(produto);
    const removidos = new Set(itemEditando.complementos.filter(c => c.tipo === 'REMOVER').map(c => c.codigoProduto));
    const adicionados = new Set(itemEditando.complementos.filter(c => c.tipo === 'ADICIONAR').map(c => c.codigoProduto));
    return produto.composicao.map(c => ({
      ...c,
      selecionado: c.tipo === 'INGREDIENTE' ? !removidos.has(c.codigoProduto) : adicionados.has(c.codigoProduto),
    }));
  }

  // pré-popula seleções de grupos a partir do item existente
  function selecoesDoItem(): Map<number, Set<number>> {
    if (!itemEditando) return new Map();
    const map = new Map<number, Set<number>>();
    for (const g of itemEditando.grupoSelecoes) {
      map.set(g.codigoGrupo, new Set(g.itens.map(i => i.codigoProduto)));
    }
    return map;
  }

  const [quantidade, setQuantidade] = useState(itemEditando?.quantidade ?? 1);
  const [observacao, setObservacao] = useState(itemEditando?.observacao ?? '');
  const [complementos, setComplementos] = useState<ComposicaoItem[]>(complementosDoItem);
  // Map<codigoGrupo, Set<codigoProduto>>
  const [selecoes, setSelecoes] = useState<Map<number, Set<number>>>(selecoesDoItem);
  const [adicionados, setAdicionados] = useState(0);
  const [pulso, setPulso] = useState(false);
  const [tentouAdicionar, setTentouAdicionar] = useState(false);

  const gruposOrdenados = useMemo(
    () => [...(produto.grupos ?? [])].sort((a, b) => a.sequencia - b.sequencia),
    [produto.grupos]
  );

  // Grupos obrigatórios que não atingiram minSelecoes
  const gruposInvalidos = useMemo(
    () =>
      gruposOrdenados.filter((g) => {
        if (!g.obrigatorio) return false;
        const qtd = selecoesDe(selecoes, g.codigo).size;
        return qtd < g.minSelecoes;
      }),
    [gruposOrdenados, selecoes]
  );

  function toggleGrupoItem(grupo: GrupoPublico, codigoProduto: number) {
    setSelecoes((prev) => {
      const next = new Map(prev);
      const atual = new Set(next.get(grupo.codigo) ?? []);

      if (atual.has(codigoProduto)) {
        atual.delete(codigoProduto);
      } else {
        if (grupo.maxSelecoes === 1) {
          // radio — substitui
          atual.clear();
        } else if (atual.size >= grupo.maxSelecoes) {
          // já no limite — não adiciona
          return prev;
        }
        atual.add(codigoProduto);
      }

      next.set(grupo.codigo, atual);
      return next;
    });
  }

  function calcExtrasGrupos(): number {
    let total = 0;
    for (const grupo of gruposOrdenados) {
      if (!grupo.alteraPreco) continue;
      const sel = selecoesDe(selecoes, grupo.codigo);
      const selecionados = grupo.itens.filter((i) => sel.has(i.codigoProduto));
      if (!selecionados.length) continue;
      if (grupo.estrategiaPreco === 'MAIOR_VALOR') {
        total += Math.max(...selecionados.map((i) => i.valorAdicional));
      } else {
        total += selecionados.reduce((s, i) => s + i.valorAdicional, 0);
      }
    }
    return total;
  }

  function calcTotal() {
    const extras = complementos
      .filter((c) => c.tipo === 'ADICIONAL' && c.selecionado)
      .reduce((s, c) => s + c.valorUnitario * c.quantidade, 0);
    return (produto.valorVenda + extras + calcExtrasGrupos()) * quantidade;
  }

  function adicionar() {
    setTentouAdicionar(true);
    if (gruposInvalidos.length > 0) return;

    const comps: ComplementoCarrinho[] = [];
    complementos
      .filter((c) => c.tipo === 'INGREDIENTE' && !c.selecionado)
      .forEach((c) =>
        comps.push({
          codigoProduto: c.codigoProduto,
          descricaoProduto: c.descricao,
          tipo: 'REMOVER',
          quantidade: c.quantidade,
          valorUnitario: 0,
        })
      );
    complementos
      .filter((c) => c.tipo === 'ADICIONAL' && c.selecionado)
      .forEach((c) =>
        comps.push({
          codigoProduto: c.codigoProduto,
          descricaoProduto: c.descricao,
          tipo: 'ADICIONAR',
          quantidade: c.quantidade,
          valorUnitario: c.valorUnitario,
        })
      );

    const grupoSelecoes: SelecaoGrupo[] = gruposOrdenados
      .map((g) => {
        const sel = selecoesDe(selecoes, g.codigo);
        const itensSelecionados = g.itens.filter((i) => sel.has(i.codigoProduto));
        return {
          codigoGrupo: g.codigo,
          tituloGrupo: g.titulo,
          estrategiaPreco: g.estrategiaPreco ?? "SOMA_ADICIONAL",
          itens: itensSelecionados.map((i) => ({
            codigoProduto: i.codigoProduto,
            descricao: i.descricao,
            valorAdicional: g.alteraPreco ? i.valorAdicional : 0,
          })),
        };
      })
      .filter((g) => g.itens.length > 0);

    const item: ItemCarrinho = {
      id: itemEditando?.id ?? uuidv4(),
      codigoProduto: produto.codigoProduto,
      descricaoProduto: produto.descricao,
      quantidade,
      valorUnitario: produto.valorVenda,
      observacao,
      complementos: comps,
      grupoSelecoes,
    };

    if (itemEditando) {
      atualizarItem(itemEditando.id, item);
      onClose();
      return;
    }

    adicionarItem(item);
    setAdicionados((n) => n + 1);

    // Reset para nova variação
    setQuantidade(1);
    setObservacao('');
    setComplementos(complementosIniciais(produto));
    setSelecoes(new Map());
    setTentouAdicionar(false);
    setPulso(true);
    setTimeout(() => setPulso(false), 600);
  }

  const ingredientes = complementos.filter((c) => c.tipo === 'INGREDIENTE');
  const adicionais = complementos.filter((c) => c.tipo === 'ADICIONAL');

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} aria-labelledby="dialog-produto-titulo">
      {produto.imagemBase64 && (
        <Box
          component="img"
          src={
            produto.imagemBase64.startsWith('data:')
              ? produto.imagemBase64
              : `data:image/jpeg;base64,${produto.imagemBase64}`
          }
          alt={produto.descricao}
          sx={{ width: '100%', maxHeight: 220, objectFit: 'cover' }}
        />
      )}

      <DialogTitle id="dialog-produto-titulo" sx={{ pr: 6 }}>
        {produto.descricao}
        <IconButton aria-label="Fechar" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Feedback após adicionar (só no modo novo) */}
        {!itemEditando && adicionados > 0 && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f0fdf4', border: '1px solid', borderColor: 'success.light',
              borderRadius: 2, px: 2, py: 1.25, mb: 2,
              transition: 'transform 0.15s',
              transform: pulso ? 'scale(1.02)' : 'scale(1)',
            }}
          >
            <CheckCircleIcon color="success" />
            <Box flex={1}>
              <Typography fontWeight={700} fontSize="0.9rem" color="success.dark">
                {adicionados === 1 ? '1 variação adicionada!' : `${adicionados} variações adicionadas!`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure abaixo para adicionar outra variação.
              </Typography>
            </Box>
            <Button
              size="small" variant="contained" color="success"
              startIcon={<ShoppingCartIcon />}
              onClick={() => { onClose(); setStep(2); }}
              sx={{ borderRadius: 2, fontWeight: 700, flexShrink: 0 }}
            >
              Ver carrinho
            </Button>
          </Box>
        )}

        {produto.descricaoDetalhada && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {produto.descricaoDetalhada}
          </Typography>
        )}

        {/* ── Grupos de composição ── */}
        {gruposOrdenados.map((grupo) => {
          const sel = selecoesDe(selecoes, grupo.codigo);
          const invalido = tentouAdicionar && grupo.obrigatorio && sel.size < grupo.minSelecoes;
          const ehRadio = grupo.maxSelecoes === 1;

          return (
            <Box key={grupo.codigo} mb={2.5}>
              {/* Cabeçalho do grupo */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  bgcolor: invalido ? 'error.50' : 'grey.100',
                  borderLeft: '3px solid',
                  borderColor: invalido ? 'error.main' : grupo.obrigatorio ? 'primary.main' : 'grey.400',
                  px: 1.5, py: 0.75, borderRadius: '0 6px 6px 0', mb: 1,
                }}
              >
                <Box>
                  <Typography fontWeight={700} fontSize="0.9rem">
                    {grupo.titulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {grupo.obrigatorio
                      ? grupo.minSelecoes === grupo.maxSelecoes
                        ? `Escolha ${grupo.maxSelecoes}`
                        : `Escolha de ${grupo.minSelecoes} a ${grupo.maxSelecoes}`
                      : grupo.maxSelecoes === 1
                      ? 'Opcional — escolha 1'
                      : `Opcional — até ${grupo.maxSelecoes}`}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.5} alignItems="center">
                  {grupo.obrigatorio && (
                    <Chip label="Obrigatório" size="small" color={invalido ? 'error' : 'primary'}
                      sx={{ fontSize: '0.65rem', height: 20 }} />
                  )}
                  {sel.size > 0 && (
                    <Chip label={`${sel.size} escolhido${sel.size > 1 ? 's' : ''}`} size="small"
                      color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
                  )}
                </Box>
              </Box>

              {invalido && (
                <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: '0.78rem' }}>
                  {grupo.minSelecoes === 1
                    ? 'Selecione pelo menos uma opção.'
                    : `Selecione pelo menos ${grupo.minSelecoes} opções.`}
                </Alert>
              )}

              {/* Itens do grupo */}
              {grupo.itens.map((item) => {
                const selecionado = sel.has(item.codigoProduto);
                // radio sempre permite clique (substitui internamente); checkbox bloqueia só quando no limite e não selecionado
                const noLimite = !ehRadio && !selecionado && sel.size >= grupo.maxSelecoes;

                return (
                  <Box
                    key={item.codigoProduto}
                    onClick={() => !noLimite && toggleGrupoItem(grupo, item.codigoProduto)}
                    sx={{
                      display: 'flex', alignItems: 'center',
                      px: 1, py: 0.5, borderRadius: 1, cursor: noLimite ? 'not-allowed' : 'pointer',
                      opacity: noLimite ? 0.45 : 1,
                      '&:hover': { bgcolor: noLimite ? 'transparent' : 'action.hover' },
                    }}
                  >
                    {ehRadio ? (
                      <Radio checked={selecionado} size="small" sx={{ p: 0.5 }} />
                    ) : (
                      <Checkbox checked={selecionado} size="small" sx={{ p: 0.5 }} />
                    )}
                    <Typography flex={1} fontSize="0.875rem" ml={0.5}>
                      {item.descricao}
                    </Typography>
                    {grupo.alteraPreco && item.valorAdicional > 0 && (
                      <Typography fontSize="0.8rem" color="primary" fontWeight={600}>
                        + {fmtMoeda(item.valorAdicional)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}

        {/* ── Ingredientes (composição base) ── */}
        {ingredientes.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Ingredientes — desmarque o que não quer
            </Typography>
            {ingredientes.map((c, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    checked={!!c.selecionado}
                    onChange={() =>
                      setComplementos((prev) =>
                        prev.map((x, xi) =>
                          xi === complementos.indexOf(c) ? { ...x, selecionado: !x.selecionado } : x
                        )
                      )
                    }
                    size="small"
                  />
                }
                label={c.descricao}
              />
            ))}
          </Box>
        )}

        {/* ── Adicionais avulsos ── */}
        {adicionais.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Adicionais
            </Typography>
            {adicionais.map((c, i) => (
              <Box key={i} display="flex" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!c.selecionado}
                      onChange={() =>
                        setComplementos((prev) =>
                          prev.map((x, xi) =>
                            xi === complementos.indexOf(c) ? { ...x, selecionado: !x.selecionado } : x
                          )
                        )
                      }
                      size="small"
                    />
                  }
                  label={c.descricao}
                />
                {c.valorUnitario > 0 && (
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    + {fmtMoeda(c.valorUnitario)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <TextField
          label="Alguma observação?"
          fullWidth
          multiline
          rows={2}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex: sem cebola, bem passado…"
          inputProps={{ 'aria-label': 'Observação para o produto' }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Quantidade + total */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={() => setQuantidade((q) => Math.max(1, q - 1))}
              aria-label="Diminuir quantidade"
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography fontWeight={700} minWidth={28} textAlign="center" fontSize="1.1rem">
              {quantidade}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setQuantidade((q) => q + 1)}
              aria-label="Aumentar quantidade"
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography fontWeight={800} color="primary" variant="h6">
            {fmtMoeda(calcTotal())}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          {itemEditando ? 'Cancelar' : adicionados > 0 ? 'Escolher outro produto' : 'Cancelar'}
        </Button>
        <Button
          variant="contained"
          onClick={adicionar}
          startIcon={<AddShoppingCartIcon />}
          sx={{ borderRadius: 2, flex: 1 }}
          aria-label={`Adicionar ${quantidade} ${produto.descricao} ao carrinho`}
        >
          {itemEditando ? 'Salvar alterações' : adicionados > 0 ? 'Adicionar outra variação' : 'Adicionar ao carrinho'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
